/* Progressive motion: stable surfaces, decoded media and no scroll-driven layout. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const mediaSelector = '.r2-card-image img,.r2-category>img,.r2-grade-grid>article img,.rc-blog-card img,.rc-hero img,.rc-split img,.rc-article>figure img';
  const contentSelector = '.r2-section-heading,.r2-why-intro,.r2-why-list article,.r2-home-faq>div,.r2-product-detail>div,.rc-film>div,.rc-story>div,.rc-services>article,.rc-grade';
  const pending = new Set(), running = new Map(), decoded = new WeakSet();
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const settle = el => {
    observer.unobserve(el);
    pending.delete(el);
    delete el.dataset.r2MotionWaiting;
    el.dataset.r2Revealed = 'true';
    running.get(el)?.cancel();
    running.delete(el);
  };
  const play = (el, frames, options) => {
    running.get(el)?.cancel();
    const animation = el.animate(frames, {...options, easing:ease, fill:'both'});
    running.set(el, animation);
    animation.finished.then(() => {
      if (running.get(el) === animation) {
        running.delete(el);
        animation.cancel(); // Release the layer and allow hover/focus styles to take over.
      }
    }).catch(() => {});
  };
  const reveal = el => {
    if (!pending.has(el)) return;
    const rect = el.getBoundingClientRect();
    if (!el.isConnected || reduced.matches || el.contains(document.activeElement) || el.closest(':target') || rect.bottom <= 0) { settle(el); return; }
    if (el.tagName === 'IMG' && (!el.complete || !el.naturalWidth || !decoded.has(el))) return;
    const media = el.dataset.r2Motion === 'media';
    settle(el);
    play(el, media ? [{scale:'.94'},{scale:'1'}] : [{translate:'0 8px'},{translate:'0 0'}], {
      duration:media ? 580 : 360,
      delay:media ? Number(el.dataset.r2Delay || 0) : 0
    });
  };
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) reveal(entry.target);
  }), {threshold:0});
  const init = scope => {
    scope.querySelectorAll(mediaSelector+','+contentSelector).forEach(el => {
      if (el.dataset.r2Motion) return;
      el.dataset.r2Motion = el.matches(mediaSelector) ? 'media' : 'content';
      const rect = el.getBoundingClientRect();
      // First-screen content and restored scroll positions render immediately.
      if (reduced.matches || (rect.top < innerHeight && rect.bottom > 0) || rect.bottom <= 0) {el.dataset.r2Revealed='true';return;}
      pending.add(el);
      if (el.dataset.r2Motion === 'media') {
        const card=el.closest('.r2-card,.r2-grade-grid>article,.rc-blog-card');
        const index=card ? [...card.parentElement.children].indexOf(card) : 0;
        el.dataset.r2Delay=String((index % (innerWidth<760?2:3))*35);
        el.dataset.r2MotionWaiting='';
        if (el.tagName === 'IMG') {
          const ready=()=>{
            const finish=()=>{
              decoded.add(el);
              if (!pending.has(el)) return;
              const r=el.getBoundingClientRect();
              if (r.bottom<=0) settle(el);
              else if(r.top<innerHeight) reveal(el);
            };
            if (el.decode) el.decode().then(finish).catch(()=>settle(el));
            else finish();
          };
          if (el.complete && el.naturalWidth) ready();
          else {el.addEventListener('load',ready,{once:true});el.addEventListener('error',()=>settle(el),{once:true});}
        }
      }
      observer.observe(el);
    });
    scope.querySelectorAll('.r2-accordions details').forEach(details => {
      if (details.dataset.r2MotionReady) return;
      details.dataset.r2MotionReady='true';
      details.addEventListener('toggle',()=>{
        [...details.children].filter(el=>el.tagName!=='SUMMARY').forEach(el=>{
          running.get(el)?.cancel();running.delete(el);
          if(details.open && !reduced.matches) play(el,[{translate:'0 4px'},{translate:'0 0'}],{duration:220});
        });
      });
    });
  };
  init(document);
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    [...pending,...running.keys()].filter(el=>event.target.contains(el)).forEach(settle);
  });
  document.addEventListener('focusin',event=>{
    [...pending,...running.keys()].filter(el=>el.contains(event.target) || event.target.contains(el)).forEach(settle);
  });
  reduced.addEventListener('change',()=>{
    if(reduced.matches) [...pending,...running.keys()].forEach(settle);
  });
  window.addEventListener('pageshow',event=>{
    if(event.persisted) [...pending,...running.keys()].forEach(settle);
  });
})();
